import { useCallback, useEffect, useRef, useState } from "react";
import { apiGet, apiPatch, ApiError } from "../api/client";
import { isoDateTimeText } from "../lib/meshmapperCoverage";
import {
  countriesText, OUTLINES_INTRO, OUTLINES_MAX_HOURS, OUTLINES_MIN_HOURS, outlinesResultText, outlinesStateText,
  parseCountries, parseOutlineHours, type RegionOutlinesStatus,
} from "../lib/meshmapperRegions";

// Settings, Regions: "Load region outlines from MeshMapper". The switch, how often to refresh (hours), the countries, and
// what the last refresh did. Saves apply at once: no restart. Switching it on loads the outlines straight away. The
// MeshMapper key is set under MeshMapper and never shown here.

type Note = { kind: "ok" | "error"; text: string } | null;

function errorText(e: unknown): string {
  if (e instanceof ApiError) {
    const m = /—\s(.+)$/.exec(e.message);
    if (m) return m[1];
    if (e.status === 403) return "Only an admin can change this.";
  }
  return "That did not save. Try again.";
}

export function MeshMapperRegionOutlines({ onLoaded }: { onLoaded?: () => void }) {
  const [status, setStatus] = useState<RegionOutlinesStatus | null>(null);
  const [loadFailed, setLoadFailed] = useState(false);
  const [hoursText, setHoursText] = useState("");
  const [countriesInput, setCountriesInput] = useState("");
  const [note, setNote] = useState<Note>(null);
  const [busy, setBusy] = useState(false);
  const hoursEdited = useRef(false);
  const countriesEdited = useRef(false);
  const lastOk = useRef<string | null | undefined>(undefined);

  const load = useCallback(async () => {
    try {
      const s = await apiGet<RegionOutlinesStatus>("/api/meshmapper/regions/status");
      setStatus(s);
      setLoadFailed(false);
      if (!hoursEdited.current) setHoursText(String(s.refresh_hours));
      if (!countriesEdited.current) setCountriesInput(countriesText(s.countries));
      if (lastOk.current !== undefined && lastOk.current !== s.last_attempt_at) onLoaded?.();
      lastOk.current = s.last_attempt_at;
    } catch {
      setLoadFailed(true);
    }
  }, [onLoaded]);

  useEffect(() => {
    load();
    const t = window.setInterval(load, 15_000);
    return () => window.clearInterval(t);
  }, [load]);

  async function save(patch: Record<string, unknown>, okText: string) {
    setBusy(true);
    setNote(null);
    try {
      await apiPatch("/api/config/mesh_regions", { patch });
      setNote({ kind: "ok", text: okText });
      await load();
    } catch (e) {
      setNote({ kind: "error", text: errorText(e) });
    } finally {
      setBusy(false);
    }
  }

  async function saveHours() {
    const n = parseOutlineHours(hoursText);
    if (n === null) {
      setNote({ kind: "error", text: `Use a whole number of hours from ${OUTLINES_MIN_HOURS} to ${OUTLINES_MAX_HOURS}.` });
      return;
    }
    if (status && n === status.refresh_hours) {
      hoursEdited.current = false;
      return;
    }
    await save({ outlines_refresh_hours: n }, "Refresh time saved.");
    hoursEdited.current = false;
  }

  async function saveCountries() {
    const list = parseCountries(countriesInput);
    if (list === null) {
      setNote({ kind: "error", text: "Use up to five two-letter country codes, such as US." });
      return;
    }
    if (status && countriesText(list) === countriesText(status.countries)) {
      countriesEdited.current = false;
      setCountriesInput(countriesText(list));
      return;
    }
    await save({ outlines_countries: list }, "Countries saved. They apply at the next refresh.");
    countriesEdited.current = false;
  }

  if (!status) {
    return <div className="mm-settings">{loadFailed ? <div className="node-detail-note">Could not load the region outline settings.</div> : <div className="empty-state">Loading…</div>}</div>;
  }

  return (
    <div className="mm-settings mm-region-outlines">
      <p className="mm-settings-intro">{OUTLINES_INTRO}</p>

      <label className="settings-field mm-settings-block mm-settings-switch">
        <input
          type="checkbox"
          checked={status.enabled}
          disabled={busy}
          onChange={(e) => save({ outlines_enabled: e.target.checked }, e.target.checked ? "Loading region outlines." : "Region outlines are off.")}
        />
        <span>Load region outlines from MeshMapper</span>
        <span className="settings-field-desc">
          Off by default; while it is off nothing is requested from MeshMapper. It also needs the MeshMapper key. Switching it on loads the outlines straight away, then once every refresh time below.
        </span>
      </label>

      <div className="settings-field mm-settings-block">
        <label htmlFor="mm-outline-hours">Refresh every (hours)</label>
        <div className="mm-settings-keyrow">
          <input
            id="mm-outline-hours"
            type="number"
            min={OUTLINES_MIN_HOURS}
            max={OUTLINES_MAX_HOURS}
            step={1}
            value={hoursText}
            onChange={(e) => { hoursEdited.current = true; setHoursText(e.target.value); }}
            onBlur={saveHours}
            onKeyDown={(e) => { if (e.key === "Enter") (e.target as HTMLInputElement).blur(); }}
          />
        </div>
        <span className="settings-field-desc">From {OUTLINES_MIN_HOURS} to {OUTLINES_MAX_HOURS}. Each refresh makes at most 10 requests.</span>
      </div>

      <div className="settings-field mm-settings-block">
        <label htmlFor="mm-outline-countries">Countries</label>
        <div className="mm-settings-keyrow">
          <input
            id="mm-outline-countries"
            type="text"
            maxLength={40}
            value={countriesInput}
            onChange={(e) => { countriesEdited.current = true; setCountriesInput(e.target.value); }}
            onBlur={saveCountries}
            onKeyDown={(e) => { if (e.key === "Enter") (e.target as HTMLInputElement).blur(); }}
          />
        </div>
        <span className="settings-field-desc">Two-letter country codes separated by commas, such as US. MeshMapper lists only the regions your key covers in these countries.</span>
      </div>

      {note && (
        <div className="settings-save-status" data-kind={note.kind} role="status">
          {note.text}
        </div>
      )}

      <h3 className="mm-settings-heading">Status</h3>
      <dl className="mm-settings-status">
        <div><dt>State</dt><dd>{outlinesStateText(status.state)}</dd></div>
        <div><dt>Last refresh</dt><dd>{isoDateTimeText(status.last_attempt_at)}{status.last_result ? ` (${outlinesResultText(status.last_result)})` : ""}</dd></div>
        <div><dt>Regions loaded</dt><dd>{status.regions_loaded}{status.regions_frozen > 0 ? `, ${status.regions_frozen} frozen (not covered by your key)` : ""}</dd></div>
        <div><dt>Next refresh</dt><dd>{status.next_refresh_at ? isoDateTimeText(status.next_refresh_at) : "not scheduled"}</dd></div>
        <div><dt>Last error</dt><dd className={status.last_error ? "mm-settings-error" : undefined}>{status.last_error ?? "none"}</dd></div>
      </dl>
    </div>
  );
}
